import React, {
 memo, useCallback, MouseEvent, useState, useEffect, useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import { Portal } from '@common/components/portal';
import { Button } from '@common/components/button';
import Icon from '@common/components/icon';
import { useOnKeyDown } from '@common/hooks';
import { Certificate } from './certificate';
import { exportCertificatePDF } from './export-certificate-pdf';
import { selected } from '@modules/organisation/selectors/organisation';
import { useSelector } from 'react-redux';

export interface CertificateViewerProps {
  isVisible: boolean;
  onClose: () => void;
  courseName: string;
  employeeName: string;
  completionDate: string;
  currentPoints: number;
  maxPoints: number;
  onExport?: () => void;
}

/**
 * Convert an image URL to base64 using the CORS proxy
 * The proxy fetches the image server-side and returns it with proper CORS headers
 */
async function convertImageToBase64(imageUrl: string): Promise<string | null> {
  if (!imageUrl || imageUrl.startsWith('data:')) {
    return imageUrl || null;
  }

  // Use the CORS proxy to fetch the image as blob, then convert to base64
  const proxyUrl = `/api/cors-image?url=${encodeURIComponent(imageUrl)}`;

  try {
    const response = await fetch(proxyUrl);

    if (!response.ok) {
      console.warn('Proxy returned error:', response.status);
      return null;
    }

    const blob = await response.blob();

    // Convert blob to base64
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        if (result && result.startsWith('data:')) {
          resolve(result);
        } else {
          resolve(null);
        }
      };
      reader.onerror = () => {
        console.warn('FileReader error');
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Failed to fetch image via proxy:', error);
    return null;
  }
}

export const CertificateViewer = memo(({
  isVisible,
  onClose,
  courseName,
  employeeName,
  completionDate,
  currentPoints,
  maxPoints,
  onExport,
}: CertificateViewerProps) => {
  const org = useSelector(selected);
  const { t } = useTranslation();

  // State to hold pre-loaded org logo as base64
  const [orgLogoBase64, setOrgLogoBase64] = useState<string | null>(null);
  const logoConversionAttempted = useRef(false);

  // Pre-load org logo as base64 when viewer opens
  useEffect(() => {
    if (!isVisible) {
      logoConversionAttempted.current = false;
      return;
    }

    const orgLogo = org?.brand_icon;
    const isWhitelabel = !!org?.whitelabel_config;

    // Only attempt conversion for whitelabel orgs with a logo
    if (!isWhitelabel || !orgLogo || logoConversionAttempted.current) {
      return;
    }

    logoConversionAttempted.current = true;

    // Try to convert the logo to base64
    convertImageToBase64(orgLogo)
      .then((base64) => {
        if (base64 && base64.startsWith('data:')) {
          setOrgLogoBase64(base64);
        }
      });
  }, [isVisible, org]);

  const handleExportPDF = useCallback(async () => {
    await exportCertificatePDF({
      courseName,
      employeeName,
      completionDate,
      currentPoints,
      maxPoints,
      org,
      preloadedOrgLogoBase64: orgLogoBase64,
    });

    onExport?.();
  }, [courseName, employeeName, completionDate, currentPoints, maxPoints, org, orgLogoBase64, onExport]);

  const handleClickContainer = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  useOnKeyDown(({ key }) => {
    if (key === 'Escape' && isVisible) {
      onClose();
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const certificateFileName = `${courseName} - ${t('learning:certificate')}.pdf`;

  return (
    <Portal>
      <div
        className={`tw-fixed tw-inset-0 tw-w-screen tw-h-screen tw-bg-black tw-bg-opacity-[0.87] tw-z-[11] tw-box-border tw-flex
            tw-flex-col`}
        onClick={handleClickContainer}
      >
        <div className="tw-flex tw-items-center tw-h-[72px] tw-px-4">
          <div
            className={`tw-flex tw-justify-center tw-items-center tw-w-9 tw-h-9 tw-rounded-md tw-bg-gray-100 tw-cursor-pointer
                tw-select-none hover:tw-bg-gray-200`}
            role="button"
            onClick={onClose}
          >
            <Icon type="close" />
          </div>
          <span className="tw-ml-3 tw-mr-auto tw-text-white tw-font-medium">
            {certificateFileName}
          </span>
          <div className="tw-flex">
            <Button
              type="primary"
              onClick={handleExportPDF}
            >
              {t('learning:export_as_pdf')}
            </Button>
          </div>
        </div>
        <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-overflow-auto">
          <div className="tw-bg-white tw-max-w-[90vw] tw-max-h-[calc(100vh-72px)] tw-overflow-auto">
            <Certificate
              courseName={courseName}
              employeeName={employeeName}
              completionDate={completionDate}
              currentPoints={currentPoints}
              maxPoints={maxPoints}
              org={org}
              orgLogoBase64={orgLogoBase64}
            />
          </div>
        </div>
      </div>
    </Portal>
  );
});

CertificateViewer.displayName = 'CertificateViewer';
